<template>
  <app-page
    title="Invoices"
    :subtitle="isDeductionsCompany ? 'View your invoices and deductions' : 'View your off cycle invoices'"
    icon="receipt"
  >
    <navigable-tabs>
      <b-tab :id="TAB_NAMES.UNPAID">
        <template #title>
          <h4>Unpaid ({{ unpaidInvoices.length }})</h4>
        </template>
        <div class="pt-4 px-2">
          <b-table class="invoices-table" :fields="UNPAID_FIELDS" :items="unpaidInvoices" table-class="table-minimal">
            <template #cell(action)="data">
              <div class="actions">
                <div
                  class="go-to"
                  :data-test="`download-invoice-${data?.item?.invoice_id}`"
                  @click="downloadInvoice(data?.item)"
                  :id="`invoice-${data?.item?.invoice_id}`"
                >
                  <b-spinner
                    v-if="isDownloading === data?.item?.invoice_id"
                    class="download-spinner"
                    variant="primary"
                  />
                  <Download :color="PaletteColors['brand-primary']" v-else />
                </div>
                <div
                  v-if="isDeductionsCompany"
                  class="go-to"
                  :data-test="`download-deductions-${data?.item?.invoice_id}`"
                  @click="downloadDeductions(data?.item)"
                >
                  <b-spinner
                    v-if="isDownloadingDeductions === data?.item?.invoice_id"
                    class="download-spinner"
                    variant="primary"
                  />
                  <font-awesome-icon v-else :icon="['far', 'file-csv']" class="csv-icon" />
                </div>
              </div>
            </template>
            <template #cell(state)="data">
              <div :class="isPast(data?.item?.due_on) ? 'overdue' : 'open'">
                {{ isPast(data?.item?.due_on) ? 'OVERDUE' : data?.item?.state }}
              </div>
            </template>
          </b-table>
        </div>
      </b-tab>

      <b-tab :id="TAB_NAMES.PAID">
        <template #title>
          <h4>Paid ({{ paidInvoices.length }})</h4>
        </template>
        <div class="pt-4 px-2">
          <b-table class="invoices-table" :fields="PAID_FIELDS" :items="paidInvoices" table-class="table-minimal">
            <template #cell(action)="data">
              <div class="actions">
                <div
                  class="go-to"
                  :data-test="`download-invoice-${data?.item?.invoice_id}`"
                  @click="downloadInvoice(data?.item)"
                  :id="`invoice-${data?.item?.invoice_id}`"
                >
                  <b-spinner
                    v-if="isDownloading === data?.item?.invoice_id"
                    class="download-spinner"
                    variant="primary"
                  />
                  <Download :color="PaletteColors['brand-primary']" v-else />
                </div>
                <div
                  v-if="isDeductionsCompany"
                  class="go-to"
                  :data-test="`download-deductions-${data?.item?.invoice_id}`"
                  @click="downloadDeductions(data?.item)"
                >
                  <b-spinner
                    v-if="isDownloadingDeductions === data?.item?.invoice_id"
                    class="download-spinner"
                    variant="primary"
                  />
                  <font-awesome-icon v-else :icon="['far', 'file-csv']" class="csv-icon" />
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </b-tab>
    </navigable-tabs>
  </app-page>
</template>

<script setup>
import Download from '@/assets/icons/Download.vue';
import AppPage from '@/components/AppPage.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import { ref, watch } from 'vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import {
  getOffCycleInvoices,
  downloadOffCycleInvoice,
  getDeductionsInvoices,
  downloadDeductionsInvoice,
  downloadDeductionsFile,
} from '@/queries/one-time-payments';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { toCurrency } from '@/utils';
import { PaletteColors } from '@/Theme';
import { format, isPast } from 'date-fns';

const TAB_NAMES = {
  UNPAID: 'unpaid',
  PAID: 'paid',
};

const { showDeductionsFiles } = useFeatureFlags();
const isDeductionsCompany = showDeductionsFiles.value;

const getInvoicesQuery = useQuery(isDeductionsCompany ? getDeductionsInvoices() : getOffCycleInvoices());
const downloadInvoiceMutation = useMutation(
  isDeductionsCompany ? downloadDeductionsInvoice() : downloadOffCycleInvoice(),
);
const downloadDeductionsMutation = useMutation(downloadDeductionsFile());

watch([getInvoicesQuery.fetchStatus, getInvoicesQuery.isPending], () => {
  if (getInvoicesQuery.data.value) {
    unpaidInvoices.value = getInvoicesQuery.data.value
      .filter(invoice => invoice.state !== 'PAID')
      .sort((a, b) => new Date(b.issued_on) - new Date(a.issued_on));
    paidInvoices.value = getInvoicesQuery.data.value
      .filter(invoice => invoice.state === 'PAID')
      .sort((a, b) => new Date(b.issued_on) - new Date(a.issued_on));
  }
});

const unpaidInvoices = ref([]);
const paidInvoices = ref([]);
const isDownloading = ref('');
const isDownloadingDeductions = ref('');

watch(downloadInvoiceMutation.isPending, isPending => {
  if (isPending) {
    isDownloading.value = downloadInvoiceMutation?.variables?.value;
  } else {
    isDownloading.value = '';
  }
});

watch(downloadDeductionsMutation.isPending, isPending => {
  isDownloadingDeductions.value = isPending ? downloadDeductionsMutation?.variables?.value : '';
});

const UNPAID_FIELDS = [
  {
    key: 'invoice_number',
    label: 'Invoice Number',
  },
  {
    key: 'issued_on',
    label: 'Issued on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'due_on',
    label: 'Due on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'state',
    label: 'State',
    tdClass: 'text-center',
    thClass: 'text-center',
  },
  {
    key: 'amount',
    label: 'Amount',
    formatter: value => toCurrency(value, 'USD'),
    tdClass: 'text-right',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'action',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
];

const PAID_FIELDS = [
  {
    key: 'invoice_number',
    label: 'Invoice Number',
  },
  {
    key: 'issued_on',
    label: 'Issued on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'paid_on',
    label: 'Paid on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'amount',
    label: 'Amount',
    formatter: value => toCurrency(value, 'USD'),
  },
  {
    key: 'action',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
];

const triggerBlobDownload = (data, filename, type) => {
  const blob = new Blob([data], { type });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', filename);
  document.body.append(link);
  link.click();
  URL.revokeObjectURL(url);
  link.remove();
};

const downloadInvoice = invoice => {
  if (downloadInvoiceMutation.isPending.value) {
    return;
  }

  const invoiceId = invoice.invoice_id;

  downloadInvoiceMutation.mutate(invoiceId, {
    onSuccess: response => triggerBlobDownload(response, `invoice_${invoice.invoice_number}.pdf`, 'application/pdf'),
    onError: error => {
      console.error('Error downloading invoice:', error);
    },
  });
};

const downloadDeductions = invoice => {
  if (downloadDeductionsMutation.isPending.value) {
    return;
  }

  downloadDeductionsMutation.mutate(invoice.invoice_id, {
    onSuccess: response => triggerBlobDownload(response, `deductions_${invoice.invoice_number}.csv`, 'text/csv'),
    onError: error => {
      console.error('Error downloading deductions file:', error);
    },
  });
};

const formatDateForTable = date => {
  if (!date) {
    return '';
  }
  return format(new Date(date), 'dd MMM yyyy');
};
</script>

<style scoped>
.actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.go-to {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    cursor: pointer;

    svg {
      fill: var(--palette-color-brand-primary);
    }
  }
}

.invoices-table {
  background-color: var(--palette-color-base-white);
  border-radius: 0.5rem;
  padding: 2rem;
  width: 100%;
}

.overdue {
  color: var(--palette-color-danger);
  background-color: var(--palette-color-danger-lighten-90);
  padding: 0.5rem;
  border-radius: 0.5rem;
  text-align: center;
  font-weight: bold;
}

.open {
  color: var(--palette-color-warning);
  background-color: var(--palette-color-warning-lighten-90);
  padding: 0.5rem;
  border-radius: 0.5rem;
  text-align: center;
  font-weight: bold;
}

.download-spinner {
  width: 1.5rem;
  height: 1.5rem;
}

.csv-icon {
  font-size: 1.25rem;
  color: var(--palette-color-brand-primary);
}
</style>
